/**
 * Financial Reporting & Statutory Control Center.
 *
 * Replaces the old FinancialReportsPage card-picker with a control-room styled
 * workspace: a financial-position snapshot, a live reconciliation / data-status
 * banner, and tabbed access to the statutory statements (Trial Balance /
 * Balance Sheet / P&L / Cash Flow), supporting Schedules, Notes-to-Accounts and
 * an Exceptions panel — all driven by real POSTED-ledger data. Statutory PDFs
 * are exported from structured statement data (jsPDF-autotable, no rasterised
 * clipping); Excel is multi-sheet.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Stack, Paper, Grid, TextField, Button, MenuItem, ToggleButtonGroup,
  ToggleButton, CircularProgress, Table, TableBody, TableCell, TableHead, TableRow,
  InputAdornment, Chip, Tooltip, Snackbar, Alert, Divider,
} from '@mui/material';
import {
  Refresh, AccountBalance, TrendingUp, AttachMoney, PeopleAlt, ReceiptLong, Search,
  CheckCircle, ErrorOutline, WarningAmber, PictureAsPdf, GridOn, OpenInNew, InsertChartOutlined,
} from '@mui/icons-material';
import { financeService } from '../../services/financeService';
import { financialReportsService, Reconciliation, FixedAssetSchedule, ControlAccountSchedule, InventorySchedule, BorrowingsSchedule, NotesToAccounts } from '../../services/financialReportsService';
import {
  TrialBalance, ScheduleIIIBalanceSheet, ScheduleIIIProfitLoss, CashFlowStatement,
  BalanceSheetCategory, PLCategory,
} from '../../types/finance';
import {
  StatCard, TableSurface, headerCellSx, bodyRowSx, StatusDot, TableEmptyState, ErrorState,
} from '../../components/common/pageKit';
import { StatementTable, StatementSection, ReportHeader } from '../../components/finance';
import { useControlRoomColors } from '../../components/common/controlRoom';
import {
  exportStatementPDF, exportMultiSheetExcel, formatIndianCurrency, StatementPdfRow, ExcelSheet,
} from '../../utils/exportUtils';

// ---------------------------------------------------------------------------
// Formatting helpers (statement conventions: parenthesised negatives, em dash)
// ---------------------------------------------------------------------------

const inr = (v: number | null | undefined): string => {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  const f = new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Math.abs(v));
  return v < 0 ? `(${f})` : f;
};
// Compact Indian notation for summary KPI tiles (exact figures stay in the
// statement tables). ≥1 Cr → "1.26 Cr", ≥1 L → "20.19 L", else grouped.
const compactInr = (v: number): string => {
  const a = Math.abs(v);
  if (a >= 1_00_00_000) return `${(v / 1_00_00_000).toFixed(2)} Cr`;
  if (a >= 1_00_000) return `${(v / 1_00_000).toFixed(2)} L`;
  if (a >= 1_000) return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(v);
  return new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);
};
const headline = (v: number | null | undefined): string => {
  if (v === null || v === undefined) return '—';
  const s = compactInr(Math.abs(v));
  return v < 0 ? `₹ (${s})` : `₹ ${s}`;
};
const fmtDate = (d?: string): string => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '');

const companyName = (): string => localStorage.getItem('companyName') || localStorage.getItem('tenantName') || 'Company';

const currentFY = () => {
  const t = new Date();
  const y = t.getFullYear();
  const m = t.getMonth() + 1;
  const startYear = m >= 4 ? y : y - 1;
  return { fromDate: `${startYear}-04-01`, toDate: `${startYear + 1}-03-31`, label: `FY${startYear}-${String(startYear + 1).slice(2)}` };
};

type ViewKey = 'overview' | 'trial-balance' | 'balance-sheet' | 'profit-loss' | 'cash-flow' | 'schedules' | 'notes' | 'exceptions';

const VIEW_TABS: { key: ViewKey; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'trial-balance', label: 'Trial Balance' },
  { key: 'balance-sheet', label: 'Balance Sheet' },
  { key: 'profit-loss', label: 'Profit & Loss' },
  { key: 'cash-flow', label: 'Cash Flow' },
  { key: 'schedules', label: 'Schedules' },
  { key: 'notes', label: 'Notes' },
  { key: 'exceptions', label: 'Exceptions' },
];

const categoriesToSections = (cats: (BalanceSheetCategory | PLCategory)[] | undefined, showComparative: boolean): StatementSection[] =>
  (cats || []).map((category, idx) => ({
    id: `${idx}-${category.name}`,
    name: category.name,
    items: category.items.map((item, i) => ({
      id: `${idx}-${i}-${item.name}`,
      label: item.name,
      current: item.current,
      comparative: showComparative ? item.comparative : undefined,
    })),
    total: category.total,
    comparativeTotal: showComparative ? category.comparativeTotal : undefined,
  }));

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const FinancialReportingControlCenter: React.FC = () => {
  const navigate = useNavigate();
  const c = useControlRoomColors();
  const line = `1px solid ${c.line}`;
  const fy = useMemo(currentFY, []);
  const today = new Date().toISOString().slice(0, 10);

  const [view, setView] = useState<ViewKey>('overview');
  const [asOnDate, setAsOnDate] = useState(today);
  const [fromDate, setFromDate] = useState(fy.fromDate);
  const [toDate, setToDate] = useState(today);
  const [compareDate, setCompareDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  // Data
  const [balanceSheet, setBalanceSheet] = useState<ScheduleIIIBalanceSheet | null>(null);
  const [profitLoss, setProfitLoss] = useState<ScheduleIIIProfitLoss | null>(null);
  const [trialBalance, setTrialBalance] = useState<TrialBalance | null>(null);
  const [cashFlow, setCashFlow] = useState<CashFlowStatement | null>(null);
  const [recon, setRecon] = useState<Reconciliation | null>(null);
  const [fixedAssets, setFixedAssets] = useState<FixedAssetSchedule | null>(null);
  const [receivables, setReceivables] = useState<ControlAccountSchedule | null>(null);
  const [payables, setPayables] = useState<ControlAccountSchedule | null>(null);
  const [inventory, setInventory] = useState<InventorySchedule | null>(null);
  const [borrowings, setBorrowings] = useState<BorrowingsSchedule | null>(null);
  const [notes, setNotes] = useState<NotesToAccounts | null>(null);

  // TB filters
  const [tbFilterType, setTbFilterType] = useState<'ALL' | 'NONZERO' | 'ASSET' | 'LIABILITY' | 'EQUITY' | 'INCOME' | 'EXPENSE'>('NONZERO');
  const [tbSearch, setTbSearch] = useState('');

  const wrapErr = (label: string) => (e: any) => { setError(`${label}: ${e?.message || 'failed to load'}`); return null; };

  // ---- Loaders ----
  const loadCore = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [bs, pl, tb, cf, rc] = await Promise.all([
        financeService.getScheduleIIIBalanceSheet(asOnDate, compareDate || undefined).catch(wrapErr('Balance Sheet')),
        financeService.getScheduleIIIProfitLoss(fromDate, toDate).catch(wrapErr('Profit & Loss')),
        financeService.getTrialBalance(asOnDate).catch(wrapErr('Trial Balance')),
        financeService.getCashFlowStatement(fromDate, toDate).catch(wrapErr('Cash Flow')),
        financialReportsService.getReconciliation(asOnDate).catch(wrapErr('Reconciliation')),
      ]);
      setBalanceSheet(bs); setProfitLoss(pl); setTrialBalance(tb); setCashFlow(cf); setRecon(rc);
    } finally {
      setLoading(false);
    }
  }, [asOnDate, fromDate, toDate, compareDate]);

  const loadSchedules = useCallback(async () => {
    setLoading(true);
    try {
      const [fa, ar, ap, inv, bor] = await Promise.all([
        financialReportsService.getFixedAssetSchedule(asOnDate, fromDate).catch(wrapErr('Fixed Assets')),
        financialReportsService.getReceivablesSchedule(asOnDate).catch(wrapErr('Receivables')),
        financialReportsService.getPayablesSchedule(asOnDate).catch(wrapErr('Payables')),
        financialReportsService.getInventorySchedule(asOnDate).catch(wrapErr('Inventory')),
        financialReportsService.getBorrowingsSchedule(asOnDate).catch(wrapErr('Borrowings')),
      ]);
      setFixedAssets(fa); setReceivables(ar); setPayables(ap); setInventory(inv); setBorrowings(bor);
    } finally {
      setLoading(false);
    }
  }, [asOnDate, fromDate]);

  const loadNotes = useCallback(async () => {
    setLoading(true);
    try {
      const n = await financialReportsService.getNotesToAccounts(asOnDate, fromDate, toDate, compareDate || undefined).catch(wrapErr('Notes'));
      setNotes(n);
    } finally {
      setLoading(false);
    }
  }, [asOnDate, fromDate, toDate, compareDate]);

  useEffect(() => { loadCore(); }, [loadCore]);
  useEffect(() => { if ((view === 'schedules') && !fixedAssets) loadSchedules(); }, [view, fixedAssets, loadSchedules]);
  useEffect(() => { if (view === 'notes' && !notes) loadNotes(); }, [view, notes, loadNotes]);

  const refreshAll = () => {
    setFixedAssets(null); setNotes(null);
    loadCore();
    if (view === 'schedules') loadSchedules();
    if (view === 'notes') loadNotes();
  };

  const showComparativeBS = Boolean(balanceSheet?.comparativeDate);
  const drill = (accountId: number) => navigate(`/finance/ledger?account=${accountId}`);

  // ---- Snapshot KPIs (real data) ----
  const findCheck = (needle: string) => recon?.checks.find((ck) => ck.label.toLowerCase().includes(needle));
  const cashAndBank = findCheck('cash & bank')?.right ?? null;
  const receivablesGL = findCheck('trade receivables')?.right ?? null;
  const payablesGL = findCheck('trade payables')?.right ?? null;
  const netProfit = profitLoss?.totals.profitAfterTax ?? null;

  // ========================================================================
  // Export builders
  // ========================================================================
  const co = companyName();

  const exportTrialBalancePDF = () => {
    if (!trialBalance) return;
    const rows: StatementPdfRow[] = [];
    (['ASSET', 'LIABILITY', 'EQUITY', 'INCOME', 'EXPENSE'] as const).forEach((type) => {
      const its = trialBalance.items.filter((i) => i.account_type === type);
      if (!its.length) return;
      rows.push({ code: '', name: type, opening: '', pdr: '', pcr: '', cdr: '', ccr: '', _emphasis: 'section' });
      its.forEach((i) => rows.push({
        code: i.account_code, name: i.account_name,
        opening: inr(i.opening_debit || -i.opening_credit),
        pdr: inr(i.period_debit), pcr: inr(i.period_credit),
        cdr: i.closing_debit ? inr(i.closing_debit) : '', ccr: i.closing_credit ? inr(i.closing_credit) : '',
      }));
    });
    rows.push({
      code: '', name: 'TOTAL', opening: '', pdr: inr(trialBalance.totals.periodDebit), pcr: inr(trialBalance.totals.periodCredit),
      cdr: inr(trialBalance.totals.closingDebit), ccr: inr(trialBalance.totals.closingCredit), _emphasis: 'total',
    });
    exportStatementPDF({
      filename: `Trial_Balance_${asOnDate}`, orientation: 'landscape', company: co,
      reportTitle: 'Trial Balance', periodLine: `As on ${fmtDate(asOnDate)}`,
      tables: [{
        columns: [
          { header: 'Code', key: 'code' }, { header: 'Account', key: 'name', align: 'left' },
          { header: 'Opening', key: 'opening' }, { header: 'Period Dr', key: 'pdr' }, { header: 'Period Cr', key: 'pcr' },
          { header: 'Closing Dr', key: 'cdr' }, { header: 'Closing Cr', key: 'ccr' },
        ],
        rows,
      }],
    });
  };

  const exportTrialBalanceExcel = () => {
    if (!trialBalance) return;
    const rows = trialBalance.items.map((i) => ({
      code: i.account_code, name: i.account_name, type: i.account_type,
      openingDr: formatIndianCurrency(i.opening_debit), openingCr: formatIndianCurrency(i.opening_credit),
      periodDr: formatIndianCurrency(i.period_debit), periodCr: formatIndianCurrency(i.period_credit),
      closingDr: formatIndianCurrency(i.closing_debit), closingCr: formatIndianCurrency(i.closing_credit),
    }));
    rows.push({
      code: '', name: 'TOTAL', type: '', openingDr: '', openingCr: '', periodDr: formatIndianCurrency(trialBalance.totals.periodDebit),
      periodCr: formatIndianCurrency(trialBalance.totals.periodCredit), closingDr: formatIndianCurrency(trialBalance.totals.closingDebit),
      closingCr: formatIndianCurrency(trialBalance.totals.closingCredit),
    } as any);
    const columns = [
      { header: 'Code', key: 'code' }, { header: 'Account', key: 'name' }, { header: 'Type', key: 'type' },
      { header: 'Opening Dr', key: 'openingDr' }, { header: 'Opening Cr', key: 'openingCr' },
      { header: 'Period Dr', key: 'periodDr' }, { header: 'Period Cr', key: 'periodCr' },
      { header: 'Closing Dr', key: 'closingDr' }, { header: 'Closing Cr', key: 'closingCr' },
    ];
    const sheets: ExcelSheet[] = [{ name: 'Trial Balance', columns, rows }];
    if (recon) sheets.push(reconSheet(recon));
    exportMultiSheetExcel(sheets, `Trial_Balance_${asOnDate}`);
  };

  const statementRowsFromCategories = (cats: (BalanceSheetCategory | PLCategory)[] | undefined, showComp: boolean): StatementPdfRow[] => {
    const rows: StatementPdfRow[] = [];
    (cats || []).forEach((cat) => {
      rows.push({ particulars: cat.name, current: '', comparative: '', _emphasis: 'section' });
      cat.items.forEach((it) => rows.push({ particulars: `    ${it.name}`, current: inr(it.current), comparative: showComp ? inr(it.comparative) : '' }));
      rows.push({ particulars: `  Sub-total — ${cat.name}`, current: inr(cat.total), comparative: showComp ? inr(cat.comparativeTotal) : '', _emphasis: 'subtotal' });
    });
    return rows;
  };

  const exportBalanceSheetPDF = () => {
    if (!balanceSheet) return;
    const showComp = showComparativeBS;
    const cols = [
      { header: 'Particulars', key: 'particulars', align: 'left' as const },
      { header: 'Current (₹)', key: 'current' as const },
      ...(showComp ? [{ header: 'Comparative (₹)', key: 'comparative' as const }] : []),
    ];
    const el = statementRowsFromCategories(balanceSheet.equityLiabilities, showComp);
    el.push({ particulars: 'TOTAL EQUITY & LIABILITIES', current: inr(balanceSheet.totalEquityLiabilities), comparative: showComp ? inr(balanceSheet.totalComparativeEquityLiabilities) : '', _emphasis: 'total' });
    const as = statementRowsFromCategories(balanceSheet.assets, showComp);
    as.push({ particulars: 'TOTAL ASSETS', current: inr(balanceSheet.totalAssets), comparative: showComp ? inr(balanceSheet.totalComparativeAssets) : '', _emphasis: 'total' });
    exportStatementPDF({
      filename: `Balance_Sheet_${asOnDate}`, orientation: 'portrait', company: co,
      reportTitle: 'Balance Sheet (Schedule III)', periodLine: `As on ${fmtDate(asOnDate)}`,
      subtitle: balanceSheet.isBalanced ? 'Balanced' : `Difference ₹ ${inr(balanceSheet.difference)}`,
      tables: [
        { caption: 'I. EQUITY AND LIABILITIES', columns: cols, rows: el },
        { caption: 'II. ASSETS', columns: cols, rows: as },
      ],
    });
  };

  const exportProfitLossPDF = () => {
    if (!profitLoss) return;
    const showComp = Boolean(profitLoss.comparativePeriod);
    const cols = [
      { header: 'Particulars', key: 'particulars', align: 'left' as const },
      { header: 'Current (₹)', key: 'current' as const },
      ...(showComp ? [{ header: 'Comparative (₹)', key: 'comparative' as const }] : []),
    ];
    const inc = statementRowsFromCategories(profitLoss.income, showComp);
    inc.push({ particulars: 'TOTAL INCOME', current: inr(profitLoss.totals.totalIncome), comparative: showComp ? inr(profitLoss.comparativeTotals?.totalIncome) : '', _emphasis: 'total' });
    const exp = statementRowsFromCategories(profitLoss.expenses, showComp);
    exp.push({ particulars: 'TOTAL EXPENSES', current: inr(profitLoss.totals.totalExpenses), comparative: showComp ? inr(profitLoss.comparativeTotals?.totalExpenses) : '', _emphasis: 'total' });
    exp.push({ particulars: 'PROFIT / (LOSS) BEFORE TAX', current: inr(profitLoss.totals.profitBeforeTax), comparative: showComp ? inr(profitLoss.comparativeTotals?.profitBeforeTax) : '', _emphasis: 'total' });
    exportStatementPDF({
      filename: `Profit_Loss_${fromDate}_to_${toDate}`, orientation: 'portrait', company: co,
      reportTitle: 'Statement of Profit and Loss (Schedule III)', periodLine: `For the period ${fmtDate(fromDate)} – ${fmtDate(toDate)}`,
      tables: [
        { caption: 'I. INCOME', columns: cols, rows: inc },
        { caption: 'II. EXPENSES', columns: cols, rows: exp },
      ],
    });
  };

  const exportCashFlowPDF = () => {
    if (!cashFlow) return;
    const rows: StatementPdfRow[] = [];
    rows.push({ particulars: 'A. Operating Activities', amount: '', _emphasis: 'section' });
    rows.push({ particulars: '    Net Profit before Tax', amount: inr(cashFlow.operatingActivities.netProfit) });
    cashFlow.operatingActivities.adjustments.forEach((a) => rows.push({ particulars: `    ${a.name}`, amount: inr(a.amount) }));
    cashFlow.operatingActivities.workingCapitalChanges.forEach((w) => rows.push({ particulars: `    ${w.name}`, amount: inr(w.amount) }));
    rows.push({ particulars: '  Net Cash from Operating Activities', amount: inr(cashFlow.operatingActivities.total), _emphasis: 'subtotal' });
    rows.push({ particulars: 'B. Investing Activities', amount: '', _emphasis: 'section' });
    cashFlow.investingActivities.items.forEach((it) => rows.push({ particulars: `    ${it.name}`, amount: inr(it.amount) }));
    rows.push({ particulars: '  Net Cash from Investing Activities', amount: inr(cashFlow.investingActivities.total), _emphasis: 'subtotal' });
    rows.push({ particulars: 'C. Financing Activities', amount: '', _emphasis: 'section' });
    cashFlow.financingActivities.items.forEach((it) => rows.push({ particulars: `    ${it.name}`, amount: inr(it.amount) }));
    rows.push({ particulars: '  Net Cash from Financing Activities', amount: inr(cashFlow.financingActivities.total), _emphasis: 'subtotal' });
    rows.push({ particulars: 'Net Increase / (Decrease) in Cash (A+B+C)', amount: inr(cashFlow.netCashFlow), _emphasis: 'total' });
    rows.push({ particulars: 'Opening Cash & Bank', amount: inr(cashFlow.openingCashBalance) });
    rows.push({ particulars: 'Closing Cash & Bank', amount: inr(cashFlow.closingCashBalance), _emphasis: 'total' });
    exportStatementPDF({
      filename: `Cash_Flow_${fromDate}_to_${toDate}`, orientation: 'portrait', company: co,
      reportTitle: 'Cash Flow Statement (AS-3 Indirect Method)', periodLine: `For the period ${fmtDate(fromDate)} – ${fmtDate(toDate)}`,
      subtitle: cashFlow.isReconciled ? 'Reconciled to Cash & Bank' : `Unreconciled by ₹ ${inr(cashFlow.reconciliationDifference)}`,
      tables: [{ columns: [{ header: 'Particulars', key: 'particulars', align: 'left' }, { header: 'Amount (₹)', key: 'amount' }], rows }],
    });
  };

  const reconSheet = (r: Reconciliation): ExcelSheet => ({
    name: 'Reconciliation',
    columns: [
      { header: 'Check', key: 'label' }, { header: 'Left', key: 'left' }, { header: 'Right', key: 'right' },
      { header: 'Difference', key: 'difference' }, { header: 'Status', key: 'status' }, { header: 'Blocking', key: 'blocking' },
    ],
    rows: r.checks.map((ck) => ({
      label: ck.label, left: formatIndianCurrency(ck.left), right: formatIndianCurrency(ck.right),
      difference: formatIndianCurrency(ck.difference), status: ck.status, blocking: ck.blocking ? 'YES' : 'no',
    })),
  });

  // ========================================================================
  // Sub-renders
  // ========================================================================

  const panelSx = { borderRadius: 3, border: line, bgcolor: c.panel } as const;

  const ReconBanner: React.FC = () => {
    if (!recon) return null;
    const s = recon.summary;
    const tone = s.overall === 'PASS' ? c.good : s.overall === 'WARN' ? c.warn : c.bad;
    const Icon = s.overall === 'PASS' ? CheckCircle : s.overall === 'WARN' ? WarningAmber : ErrorOutline;
    return (
      <Paper elevation={0} sx={{ ...panelSx, p: 1.75, mb: 2, borderColor: tone }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 220 }}>
            <Icon sx={{ color: tone }} />
            <Box>
              <Typography sx={{ fontWeight: 800, color: tone, lineHeight: 1.1 }}>
                {s.overall === 'PASS' ? 'All controls tie' : s.overall === 'WARN' ? 'Ties, with warnings' : 'Structural break'}
              </Typography>
              <Typography variant="caption" sx={{ color: c.sub }}>
                {s.passed}/{s.total} passed · {s.warnings} warning(s){s.blockingFailures ? ` · ${s.blockingFailures} blocking` : ''}
              </Typography>
            </Box>
          </Stack>
          <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' }, borderColor: c.line }} />
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ flex: 1 }}>
            {recon.checks.map((ck) => (
              <Tooltip key={ck.label} title={`${ck.label}: L ${inr(ck.left)} vs R ${inr(ck.right)} · diff ${inr(ck.difference)}`}>
                <Chip
                  size="small"
                  icon={ck.status === 'PASS' ? <CheckCircle sx={{ fontSize: 15 }} /> : <WarningAmber sx={{ fontSize: 15 }} />}
                  label={ck.label.replace(/ \(.*\)/, '').replace(/ =.*/, '')}
                  sx={{
                    bgcolor: 'transparent', border: `1px solid ${ck.status === 'PASS' ? c.good : c.warn}`,
                    color: ck.status === 'PASS' ? c.good : c.warn, fontWeight: 600, '& .MuiChip-icon': { color: 'inherit' },
                  }}
                />
              </Tooltip>
            ))}
          </Stack>
          <Button size="small" variant="outlined" onClick={() => setView('exceptions')} sx={{ borderColor: c.line, color: c.text }}>
            View exceptions
          </Button>
        </Stack>
      </Paper>
    );
  };

  const renderOverview = () => {
    const navItems: { label: string; hint: string; view?: ViewKey; to?: string; icon: React.ReactNode }[] = [
      { label: 'Trial Balance', hint: 'Netted balances, balanced', view: 'trial-balance', icon: <InsertChartOutlined /> },
      { label: 'Balance Sheet', hint: 'Schedule III position', view: 'balance-sheet', icon: <AccountBalance /> },
      { label: 'Profit & Loss', hint: 'Schedule III performance', view: 'profit-loss', icon: <TrendingUp /> },
      { label: 'Cash Flow', hint: 'AS-3 indirect', view: 'cash-flow', icon: <AttachMoney /> },
      { label: 'Schedules', hint: 'FA · AR · AP · Inventory · Borrowings', view: 'schedules', icon: <ReceiptLong /> },
      { label: 'Notes to Accounts', hint: 'Note-to-line index', view: 'notes', icon: <ReceiptLong /> },
      { label: 'Budget vs Actual', hint: 'Management view', to: '/finance/budgets', icon: <InsertChartOutlined /> },
      { label: 'Cost / Profit Centre P&L', hint: 'Management view', to: '/finance/cost-centers', icon: <PeopleAlt /> },
    ];
    return (
      <Box>
        <ReconBanner />
        <Grid container spacing={1.75} sx={{ mb: 2 }}>
          <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
            <StatCard title="Total Assets" value={headline(balanceSheet?.totalAssets)} icon={<AccountBalance />} color="#7c3aed" onClick={() => setView('balance-sheet')} />
          </Grid>
          <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
            <StatCard title={netProfit != null && netProfit < 0 ? 'Net Loss (FY)' : 'Net Profit (FY)'} value={headline(netProfit)} icon={<TrendingUp />} color={netProfit != null && netProfit < 0 ? '#dc2626' : '#16a34a'} onClick={() => setView('profit-loss')} />
          </Grid>
          <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
            <StatCard title="Cash & Bank" value={headline(cashAndBank)} icon={<AttachMoney />} color="#0284c7" onClick={() => setView('cash-flow')} />
          </Grid>
          <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
            <StatCard title="Receivables (GL)" value={headline(receivablesGL)} icon={<PeopleAlt />} color="#0891b2" onClick={() => setView('schedules')} />
          </Grid>
          <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
            <StatCard title="Payables (GL)" value={headline(payablesGL)} icon={<ReceiptLong />} color="#ea580c" onClick={() => setView('schedules')} />
          </Grid>
        </Grid>

        <Typography variant="subtitle2" sx={{ color: c.sub, fontWeight: 700, mb: 1 }}>Statements, schedules & management views</Typography>
        <Grid container spacing={1.5}>
          {navItems.map((n) => (
            <Grid key={n.label} size={{ xs: 12, sm: 6, md: 3 }}>
              <Paper
                elevation={0}
                onClick={() => (n.view ? setView(n.view) : n.to ? navigate(n.to) : undefined)}
                sx={{ ...panelSx, p: 1.75, cursor: 'pointer', height: '100%', transition: 'transform .15s, box-shadow .15s', '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 8px 20px ${c.line}` } }}
              >
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <Box sx={{ width: 34, height: 34, borderRadius: '50%', display: 'grid', placeItems: 'center', bgcolor: c.panel2, color: c.accent }}>{n.icon}</Box>
                  <Box>
                    <Typography sx={{ fontWeight: 700, color: c.text, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {n.label} {n.to && <OpenInNew sx={{ fontSize: 13, color: c.sub }} />}
                    </Typography>
                    <Typography variant="caption" sx={{ color: c.sub }}>{n.hint}</Typography>
                  </Box>
                </Stack>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  const renderTrialBalance = () => {
    if (!trialBalance) return <TableEmptyState title="No data" hint="Adjust the as-on date and refresh." />;
    let items = trialBalance.items;
    if (tbFilterType === 'NONZERO') items = items.filter((i) => i.closing_debit !== 0 || i.closing_credit !== 0);
    else if (tbFilterType !== 'ALL') items = items.filter((i) => i.account_type === tbFilterType);
    if (tbSearch.trim()) {
      const s = tbSearch.trim().toLowerCase();
      items = items.filter((i) => i.account_code.toLowerCase().includes(s) || i.account_name.toLowerCase().includes(s));
    }
    const balanced = Math.abs((trialBalance.totals.difference ?? (trialBalance.totals.closingDebit - trialBalance.totals.closingCredit))) < 0.01;
    return (
      <Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }} alignItems="center">
          <ToggleButtonGroup size="small" exclusive value={tbFilterType} onChange={(_, v) => v && setTbFilterType(v)}>
            {['ALL', 'NONZERO', 'ASSET', 'LIABILITY', 'EQUITY', 'INCOME', 'EXPENSE'].map((t) => (
              <ToggleButton key={t} value={t} sx={{ textTransform: 'none', color: c.text, borderColor: c.line }}>{t === 'NONZERO' ? 'Non-zero' : t.charAt(0) + t.slice(1).toLowerCase()}</ToggleButton>
            ))}
          </ToggleButtonGroup>
          <TextField size="small" placeholder="Search code / name" value={tbSearch} onChange={(e) => setTbSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 18 }} /></InputAdornment> }} sx={{ minWidth: 220 }} />
          <Box sx={{ flex: 1 }} />
          <Chip icon={balanced ? <CheckCircle sx={{ fontSize: 16 }} /> : <ErrorOutline sx={{ fontSize: 16 }} />} label={balanced ? 'Balanced' : `Diff ₹ ${inr(trialBalance.totals.closingDebit - trialBalance.totals.closingCredit)}`}
            sx={{ bgcolor: 'transparent', border: `1px solid ${balanced ? c.good : c.bad}`, color: balanced ? c.good : c.bad, fontWeight: 700, '& .MuiChip-icon': { color: 'inherit' } }} />
          <Button size="small" startIcon={<PictureAsPdf />} onClick={exportTrialBalancePDF} sx={{ color: c.text }}>PDF</Button>
          <Button size="small" startIcon={<GridOn />} onClick={exportTrialBalanceExcel} sx={{ color: c.text }}>Excel</Button>
        </Stack>
        <TableSurface>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellSx}>Code</TableCell>
                <TableCell sx={headerCellSx}>Account</TableCell>
                <TableCell sx={headerCellSx}>Type</TableCell>
                <TableCell sx={{ ...headerCellSx, textAlign: 'right' }}>Opening</TableCell>
                <TableCell sx={{ ...headerCellSx, textAlign: 'right' }}>Period Dr</TableCell>
                <TableCell sx={{ ...headerCellSx, textAlign: 'right' }}>Period Cr</TableCell>
                <TableCell sx={{ ...headerCellSx, textAlign: 'right' }}>Closing</TableCell>
                <TableCell sx={{ ...headerCellSx, textAlign: 'center' }}>Dr/Cr</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.length === 0 ? (
                <TableRow><TableCell colSpan={8}><TableEmptyState title="No accounts" hint="No accounts match this filter." /></TableCell></TableRow>
              ) : items.map((i) => {
                const isDr = i.closing_debit !== 0 || (i.closing_debit === 0 && i.closing_credit === 0);
                const closing = i.closing_debit || i.closing_credit;
                return (
                  <TableRow key={i.account_id} hover onClick={() => drill(i.account_id)} sx={{ ...bodyRowSx, cursor: 'pointer' }}>
                    <TableCell sx={{ fontFamily: 'monospace', color: c.sub }}>{i.account_code}</TableCell>
                    <TableCell sx={{ color: c.text, fontWeight: 500 }}>{i.account_name}</TableCell>
                    <TableCell sx={{ color: c.sub }}>{i.account_type}</TableCell>
                    <TableCell sx={{ textAlign: 'right', color: c.sub }}>{inr(i.opening_debit || -i.opening_credit)}</TableCell>
                    <TableCell sx={{ textAlign: 'right', color: c.text }}>{i.period_debit ? inr(i.period_debit) : '—'}</TableCell>
                    <TableCell sx={{ textAlign: 'right', color: c.text }}>{i.period_credit ? inr(i.period_credit) : '—'}</TableCell>
                    <TableCell sx={{ textAlign: 'right', color: c.text, fontWeight: 600 }}>{inr(closing)}</TableCell>
                    <TableCell sx={{ textAlign: 'center', color: i.closing_credit ? c.bad : c.good, fontWeight: 700 }}>{closing === 0 ? '—' : i.closing_credit ? 'Cr' : 'Dr'}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableSurface>
        <Stack direction="row" justifyContent="flex-end" spacing={3} sx={{ mt: 1.5, pr: 1 }}>
          <Typography sx={{ color: c.sub }}>Closing Dr: <b style={{ color: c.text }}>{inr(trialBalance.totals.closingDebit)}</b></Typography>
          <Typography sx={{ color: c.sub }}>Closing Cr: <b style={{ color: c.text }}>{inr(trialBalance.totals.closingCredit)}</b></Typography>
        </Stack>
      </Box>
    );
  };

  const renderBalanceSheet = () => {
    if (!balanceSheet) return <TableEmptyState title="No data" hint="Adjust the as-on date and refresh." />;
    return (
      <Box id="balance-sheet-report">
        <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
          <Box sx={{ flex: 1 }} />
          <Button size="small" startIcon={<PictureAsPdf />} onClick={exportBalanceSheetPDF} sx={{ color: c.text }}>PDF</Button>
          <Button size="small" startIcon={<GridOn />} onClick={() => {
            const showComp = showComparativeBS;
            const rows = [...statementRowsFromCategories(balanceSheet.equityLiabilities, showComp),
              { particulars: 'TOTAL EQUITY & LIABILITIES', current: formatIndianCurrency(balanceSheet.totalEquityLiabilities), comparative: '' },
              ...statementRowsFromCategories(balanceSheet.assets, showComp),
              { particulars: 'TOTAL ASSETS', current: formatIndianCurrency(balanceSheet.totalAssets), comparative: '' }];
            exportMultiSheetExcel([{ name: 'Balance Sheet', columns: [{ header: 'Particulars', key: 'particulars' }, { header: 'Current', key: 'current' }, { header: 'Comparative', key: 'comparative' }], rows: rows as any }], `Balance_Sheet_${asOnDate}`);
          }} sx={{ color: c.text }}>Excel</Button>
        </Stack>
        <ReportHeader companyName={co} reportTitle="Balance Sheet (Schedule III)" asOnDate={balanceSheet.asOnDate}
          subtitle={showComparativeBS ? `Comparative to ${fmtDate(balanceSheet.comparativeDate as string)}` : undefined} />
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: c.text }}>Equity and Liabilities</Typography>
            <StatementTable sections={categoriesToSections(balanceSheet.equityLiabilities, showComparativeBS)} showComparative={showComparativeBS} showVariance={showComparativeBS}
              currentLabel="Current" comparativeLabel="Comparative" grandTotalLabel="Total Equity & Liabilities" grandTotal={balanceSheet.totalEquityLiabilities}
              grandComparativeTotal={showComparativeBS ? balanceSheet.totalComparativeEquityLiabilities : undefined} accent="#7c3aed" emptyHint="No mapped accounts." />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: c.text }}>Assets</Typography>
            <StatementTable sections={categoriesToSections(balanceSheet.assets, showComparativeBS)} showComparative={showComparativeBS} showVariance={showComparativeBS}
              currentLabel="Current" comparativeLabel="Comparative" grandTotalLabel="Total Assets" grandTotal={balanceSheet.totalAssets}
              grandComparativeTotal={showComparativeBS ? balanceSheet.totalComparativeAssets : undefined} accent="#7c3aed" emptyHint="No mapped accounts." />
          </Grid>
        </Grid>
        <Paper elevation={0} sx={{ ...panelSx, p: 1.5, mt: 2, borderColor: balanceSheet.isBalanced ? c.good : c.bad }}>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
            {balanceSheet.isBalanced ? <CheckCircle sx={{ color: c.good }} /> : <ErrorOutline sx={{ color: c.bad }} />}
            <Typography sx={{ fontWeight: 700, color: balanceSheet.isBalanced ? c.good : c.bad }}>
              {balanceSheet.isBalanced ? 'Balance Sheet balances' : 'Out of balance'}
            </Typography>
            <Typography sx={{ color: c.sub }}>Assets <b style={{ color: c.text }}>₹ {inr(balanceSheet.totalAssets)}</b></Typography>
            <Typography sx={{ color: c.sub }}>Equity & Liabilities <b style={{ color: c.text }}>₹ {inr(balanceSheet.totalEquityLiabilities)}</b></Typography>
            <Typography sx={{ color: c.sub }}>Difference <b style={{ color: balanceSheet.isBalanced ? c.good : c.bad }}>₹ {inr(balanceSheet.difference)}</b></Typography>
            {balanceSheet.unappropriatedSurplus && (
              <Typography sx={{ color: c.sub }}>incl. Surplus / (Deficit) <b style={{ color: c.text }}>₹ {inr(balanceSheet.unappropriatedSurplus.current)}</b></Typography>
            )}
          </Stack>
        </Paper>
      </Box>
    );
  };

  const renderProfitLoss = () => {
    if (!profitLoss) return <TableEmptyState title="No data" hint="Adjust the period and refresh." />;
    const showComp = Boolean(profitLoss.comparativePeriod);
    return (
      <Box id="profit-loss-report">
        <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
          <Box sx={{ flex: 1 }} />
          <Button size="small" startIcon={<PictureAsPdf />} onClick={exportProfitLossPDF} sx={{ color: c.text }}>PDF</Button>
        </Stack>
        <ReportHeader companyName={co} reportTitle="Statement of Profit and Loss (Schedule III)" fromDate={profitLoss.period?.fromDate} toDate={profitLoss.period?.toDate} />
        <Grid container spacing={1.75} sx={{ mb: 2 }}>
          {[
            { t: 'Total Income', v: profitLoss.totals.totalIncome, cmp: profitLoss.comparativeTotals?.totalIncome, col: '#16a34a' },
            { t: 'Total Expenses', v: profitLoss.totals.totalExpenses, cmp: profitLoss.comparativeTotals?.totalExpenses, col: '#ea580c' },
            { t: 'Profit Before Tax', v: profitLoss.totals.profitBeforeTax, cmp: profitLoss.comparativeTotals?.profitBeforeTax, col: profitLoss.totals.profitBeforeTax >= 0 ? '#16a34a' : '#dc2626' },
            { t: 'Profit After Tax', v: profitLoss.totals.profitAfterTax, cmp: profitLoss.comparativeTotals?.profitAfterTax, col: profitLoss.totals.profitAfterTax >= 0 ? '#16a34a' : '#dc2626' },
          ].map((k) => (
            <Grid key={k.t} size={{ xs: 6, md: 3 }}>
              <StatCard title={k.t} value={headline(k.v)} icon={<TrendingUp />} color={k.col}
                footer={showComp ? <Typography variant="caption" sx={{ color: c.sub }}>Prior: ₹ {inr(k.cmp)}</Typography> : undefined} />
            </Grid>
          ))}
        </Grid>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: '#16a34a' }}>Income</Typography>
            <StatementTable sections={categoriesToSections(profitLoss.income, showComp)} showComparative={showComp} showVariance={showComp}
              currentLabel="Current" comparativeLabel="Comparative" grandTotalLabel="Total Income" grandTotal={profitLoss.totals.totalIncome}
              grandComparativeTotal={showComp ? profitLoss.comparativeTotals?.totalIncome : undefined} accent="#16a34a" emptyHint="No mapped income accounts." />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: '#ea580c' }}>Expenses</Typography>
            <StatementTable sections={categoriesToSections(profitLoss.expenses, showComp)} showComparative={showComp} showVariance={showComp}
              currentLabel="Current" comparativeLabel="Comparative" grandTotalLabel="Total Expenses" grandTotal={profitLoss.totals.totalExpenses}
              grandComparativeTotal={showComp ? profitLoss.comparativeTotals?.totalExpenses : undefined} accent="#ea580c" emptyHint="No mapped expense accounts." />
          </Grid>
        </Grid>
      </Box>
    );
  };

  const renderCashFlow = () => {
    if (!cashFlow) return <TableEmptyState title="No data" hint="Adjust the period and refresh." />;
    const sections: StatementSection[] = [
      {
        id: 'op', name: 'A. Operating Activities',
        items: [
          { id: 'np', label: 'Net Profit before Tax', current: cashFlow.operatingActivities.netProfit },
          ...cashFlow.operatingActivities.adjustments.map((a, i) => ({ id: `adj${i}`, label: a.name, current: a.amount })),
          ...cashFlow.operatingActivities.workingCapitalChanges.map((w, i) => ({ id: `wc${i}`, label: w.name, current: w.amount })),
        ],
        total: cashFlow.operatingActivities.total, totalLabel: 'Net Cash from Operating',
      },
      { id: 'inv', name: 'B. Investing Activities', items: cashFlow.investingActivities.items.map((it, i) => ({ id: `iv${i}`, label: it.name, current: it.amount })), total: cashFlow.investingActivities.total, totalLabel: 'Net Cash from Investing' },
      { id: 'fin', name: 'C. Financing Activities', items: cashFlow.financingActivities.items.map((it, i) => ({ id: `fn${i}`, label: it.name, current: it.amount })), total: cashFlow.financingActivities.total, totalLabel: 'Net Cash from Financing' },
    ];
    const rec = cashFlow.reconciliation;
    return (
      <Box id="cash-flow-report">
        <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
          <Box sx={{ flex: 1 }} />
          <Button size="small" startIcon={<PictureAsPdf />} onClick={exportCashFlowPDF} sx={{ color: c.text }}>PDF</Button>
        </Stack>
        <ReportHeader companyName={co} reportTitle="Cash Flow Statement (AS-3 Indirect Method)" fromDate={cashFlow.period?.fromDate} toDate={cashFlow.period?.toDate} />
        <StatementTable sections={sections} grandTotalLabel="Net Increase / (Decrease) in Cash (A+B+C)" grandTotal={cashFlow.netCashFlow} accent="#ea580c" emptyHint="No cash flow activity." />
        <Paper elevation={0} sx={{ ...panelSx, p: 1.5, mt: 2, borderColor: cashFlow.isReconciled ? c.good : c.warn }}>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
            {cashFlow.isReconciled ? <CheckCircle sx={{ color: c.good }} /> : <WarningAmber sx={{ color: c.warn }} />}
            <Typography sx={{ fontWeight: 700, color: cashFlow.isReconciled ? c.good : c.warn }}>{cashFlow.isReconciled ? 'Reconciled' : 'Not reconciled'}</Typography>
            <Typography sx={{ color: c.sub }}>Opening <b style={{ color: c.text }}>₹ {inr(rec?.openingCash)}</b></Typography>
            <Typography sx={{ color: c.sub }}>+ Net flow <b style={{ color: c.text }}>₹ {inr(rec?.netCashFlow)}</b></Typography>
            <Typography sx={{ color: c.sub }}>= Computed <b style={{ color: c.text }}>₹ {inr(rec?.computedClosing)}</b></Typography>
            <Typography sx={{ color: c.sub }}>Actual closing <b style={{ color: c.text }}>₹ {inr(rec?.actualClosing)}</b></Typography>
            <Typography sx={{ color: c.sub }}>Diff <b style={{ color: cashFlow.isReconciled ? c.good : c.warn }}>₹ {inr(rec?.difference)}</b></Typography>
          </Stack>
        </Paper>
      </Box>
    );
  };

  const ScheduleCard: React.FC<{ title: string; children: React.ReactNode; onExport?: () => void }> = ({ title, children }) => (
    <Paper elevation={0} sx={{ ...panelSx, p: 2, height: '100%' }}>
      <Typography sx={{ fontWeight: 700, color: c.text, mb: 1.25 }}>{title}</Typography>
      {children}
    </Paper>
  );
  const SLine: React.FC<{ label: string; value: number | null | undefined; strong?: boolean }> = ({ label, value, strong }) => (
    <Stack direction="row" justifyContent="space-between" sx={{ py: 0.5, borderBottom: `1px dashed ${c.line}` }}>
      <Typography variant="body2" sx={{ color: strong ? c.text : c.sub, fontWeight: strong ? 700 : 400 }}>{label}</Typography>
      <Typography variant="body2" sx={{ color: c.text, fontWeight: strong ? 800 : 500 }}>{inr(value)}</Typography>
    </Stack>
  );

  const renderSchedules = () => (
    <Grid container spacing={1.75}>
      <Grid size={{ xs: 12, md: 6 }}>
        <ScheduleCard title="Fixed Asset Movement (PPE)">
          {fixedAssets ? (
            <>
              <SLine label="Opening Gross Block" value={fixedAssets.grossBlock.opening} />
              <SLine label="Additions" value={fixedAssets.grossBlock.additions} />
              <SLine label="Disposals" value={fixedAssets.grossBlock.disposals} />
              <SLine label="Closing Gross Block" value={fixedAssets.grossBlock.closing} strong />
              <SLine label="Accumulated Depreciation (opening)" value={fixedAssets.depreciation.opening} />
              <SLine label="Depreciation for period" value={fixedAssets.depreciation.chargeForPeriod} />
              <SLine label="Accumulated Depreciation (closing)" value={fixedAssets.depreciation.closing} />
              <SLine label="Net Block" value={fixedAssets.netBlock} strong />
            </>
          ) : <CircularProgress size={20} />}
        </ScheduleCard>
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <ScheduleCard title="Inventory & Borrowings">
          {inventory ? (<>
            {inventory.items.map((it) => <SLine key={it.code} label={`${it.name} (${it.code})`} value={it.amount} />)}
            <SLine label="Total Inventory (GL)" value={inventory.glTotal} strong />
            {inventory.valuationSnapshotTotal != null && <SLine label="Valuation snapshot" value={inventory.valuationSnapshotTotal} />}
          </>) : <CircularProgress size={20} />}
          <Box sx={{ height: 8 }} />
          {borrowings ? (<>
            {borrowings.items.map((it) => <SLine key={it.code} label={`${it.name} (${it.code})`} value={it.amount} />)}
            <SLine label="Total Borrowings" value={borrowings.total} strong />
          </>) : <CircularProgress size={20} />}
        </ScheduleCard>
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <ScheduleCard title="Trade Receivables — Ageing vs GL">
          {receivables ? <AgeingBlock sch={receivables} /> : <CircularProgress size={20} />}
        </ScheduleCard>
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <ScheduleCard title="Trade Payables — Ageing vs GL">
          {payables ? <AgeingBlock sch={payables} /> : <CircularProgress size={20} />}
        </ScheduleCard>
      </Grid>
    </Grid>
  );

  const AgeingBlock: React.FC<{ sch: ControlAccountSchedule }> = ({ sch }) => {
    const b = sch.ageing.totals;
    return (
      <>
        <SLine label="0–30 days (current)" value={b.current} />
        <SLine label="31–60 days" value={b.days31_60} />
        <SLine label="61–90 days" value={b.days61_90} />
        <SLine label="91–120 days" value={b.days91_120} />
        <SLine label="Over 120 days" value={b.over120} />
        <SLine label="Sub-ledger Total" value={sch.subLedgerTotal} strong />
        <SLine label={`GL ${sch.controlAccount.code} ${sch.controlAccount.name}`} value={sch.controlAccount.glBalance} />
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
          <StatusDot tone={sch.tiesToGL ? 'success' : 'warning'} label={sch.tiesToGL ? 'Ties to GL' : `Does not tie · diff ₹ ${inr(sch.difference)}`} />
        </Stack>
      </>
    );
  };

  const renderNotes = () => {
    if (!notes) return <CircularProgress size={22} />;
    return (
      <TableSurface>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={headerCellSx}>Note</TableCell>
              <TableCell sx={headerCellSx}>Particulars</TableCell>
              <TableCell sx={headerCellSx}>Statement</TableCell>
              <TableCell sx={{ ...headerCellSx, textAlign: 'right' }}>Current (₹)</TableCell>
              <TableCell sx={{ ...headerCellSx, textAlign: 'right' }}>Previous (₹)</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {notes.notes.map((n) => (
              <TableRow key={n.note} sx={bodyRowSx}>
                <TableCell sx={{ color: c.sub, fontWeight: 700 }}>{n.note}</TableCell>
                <TableCell sx={{ color: c.text, fontWeight: 500 }}>{n.title}</TableCell>
                <TableCell sx={{ color: c.sub }}>{n.statement === 'BALANCE_SHEET' ? 'Balance Sheet' : 'P&L'}</TableCell>
                <TableCell sx={{ textAlign: 'right', color: c.text, fontWeight: 600 }}>{inr(n.current)}</TableCell>
                <TableCell sx={{ textAlign: 'right', color: c.sub }}>{n.previous == null ? '—' : inr(n.previous)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableSurface>
    );
  };

  const renderExceptions = () => {
    if (!recon) return <CircularProgress size={22} />;
    return (
      <Box>
        <Typography variant="body2" sx={{ color: c.sub, mb: 1.5 }}>{recon.notes}</Typography>
        <TableSurface>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellSx}>Control Check</TableCell>
                <TableCell sx={{ ...headerCellSx, textAlign: 'right' }}>Left</TableCell>
                <TableCell sx={{ ...headerCellSx, textAlign: 'right' }}>Right</TableCell>
                <TableCell sx={{ ...headerCellSx, textAlign: 'right' }}>Difference</TableCell>
                <TableCell sx={{ ...headerCellSx, textAlign: 'center' }}>Status</TableCell>
                <TableCell sx={{ ...headerCellSx, textAlign: 'center' }}>Severity</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recon.checks.map((ck) => (
                <TableRow key={ck.label} sx={bodyRowSx}>
                  <TableCell sx={{ color: c.text, fontWeight: 500 }}>{ck.label}</TableCell>
                  <TableCell sx={{ textAlign: 'right', color: c.sub }}>{inr(ck.left)}</TableCell>
                  <TableCell sx={{ textAlign: 'right', color: c.sub }}>{inr(ck.right)}</TableCell>
                  <TableCell sx={{ textAlign: 'right', color: ck.status === 'PASS' ? c.good : c.warn, fontWeight: 700 }}>{inr(ck.difference)}</TableCell>
                  <TableCell sx={{ textAlign: 'center' }}><StatusDot tone={ck.status === 'PASS' ? 'success' : 'warning'} label={ck.status} /></TableCell>
                  <TableCell sx={{ textAlign: 'center', color: ck.blocking ? c.bad : c.sub, fontWeight: ck.blocking ? 700 : 400 }}>{ck.blocking ? 'Blocking' : 'Warning'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableSurface>
      </Box>
    );
  };

  // ========================================================================
  // Shell
  // ========================================================================
  const usePeriod = view === 'profit-loss' || view === 'cash-flow';

  return (
    <Box sx={{ p: 3, bgcolor: c.bg, minHeight: '100%' }}>
      {/* Sticky control header offset for the app shell */}
      <Box sx={{ position: 'sticky', top: { xs: 32, sm: 64 }, zIndex: 3, bgcolor: c.bg, pb: 1.5, mb: 1.5, borderBottom: line }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} spacing={1.5}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, color: c.text, lineHeight: 1.1 }}>Financial Reporting & Statutory Control Center</Typography>
            <Typography variant="body2" sx={{ color: c.sub }}>Companies Act 2013 · Schedule III · {fy.label} · every figure ties to the POSTED ledger</Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <TextField size="small" type="date" label="As on" value={asOnDate} onChange={(e) => setAsOnDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 160 }} />
            {usePeriod && <>
              <TextField size="small" type="date" label="From" value={fromDate} onChange={(e) => setFromDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 150 }} />
              <TextField size="small" type="date" label="To" value={toDate} onChange={(e) => setToDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 150 }} />
            </>}
            {view === 'balance-sheet' && <TextField size="small" type="date" label="Compare" value={compareDate} onChange={(e) => setCompareDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 150 }} />}
            <Button variant="contained" size="small" startIcon={loading ? <CircularProgress size={15} /> : <Refresh />} onClick={refreshAll} disabled={loading}>Refresh</Button>
          </Stack>
        </Stack>
        <ToggleButtonGroup size="small" exclusive value={view} onChange={(_, v) => v && setView(v)} sx={{ mt: 1.5, flexWrap: 'wrap' }}>
          {VIEW_TABS.map((t) => (
            <ToggleButton key={t.key} value={t.key} sx={{ textTransform: 'none', color: c.text, borderColor: c.line, '&.Mui-selected': { bgcolor: c.panel2, color: c.accent, fontWeight: 700 } }}>{t.label}</ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {error && <ErrorState message={error} onRetry={refreshAll} />}

      {view === 'overview' && renderOverview()}
      {view === 'trial-balance' && renderTrialBalance()}
      {view === 'balance-sheet' && renderBalanceSheet()}
      {view === 'profit-loss' && renderProfitLoss()}
      {view === 'cash-flow' && renderCashFlow()}
      {view === 'schedules' && renderSchedules()}
      {view === 'notes' && renderNotes()}
      {view === 'exceptions' && renderExceptions()}

      <Snackbar open={Boolean(toast)} autoHideDuration={3000} onClose={() => setToast(null)}>
        <Alert severity="success" onClose={() => setToast(null)}>{toast}</Alert>
      </Snackbar>
    </Box>
  );
};

export default FinancialReportingControlCenter;
